import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "@/services/firebase";
import { useAuth } from "@/contexts/AuthContext";
import type { SupportRequest } from "./lifecycle";

/**
 * Requests concerning this institution, live.
 *
 * A subscription rather than a fetch, because the banner has to appear the
 * moment somebody asks and disappear the moment the window closes — an owner
 * who has to reload to find out that Spontar is looking at their data has not
 * really been told.
 */
export function useSupportRequests(): { requests: SupportRequest[]; loading: boolean } {
  const { user } = useAuth();
  const institutionId = user?.institutionId ?? undefined;
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!institutionId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    return onSnapshot(
      query(collection(db(), "supportRequests"), where("institutionId", "==", institutionId)),
      (snapshot) => {
        setRequests(
          snapshot.docs
            .map((document) => document.data() as SupportRequest)
            .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        );
        setLoading(false);
      },
      () => {
        setRequests([]);
        setLoading(false);
      }
    );
  }, [institutionId]);

  return { requests, loading };
}
